/**
 * Wspólna logika KOLEJNOŚCI pól/kolumn wewnątrz formatki (blok kreatora layoutu).
 *
 * Formatki (adres macierzysty = pola; listy adresów/kontaktów/kont = kolumny) pozwalają
 * ukrywać wiersze ikonką oka; TERAZ dochodzi też zmiana KOLEJNOŚCI wierszy (drag). Kolejność
 * trzymana jest w `element.settings` pod dedykowanym kluczem (`fieldOrder` / `columnOrder`) jako
 * lista slugów. Kolejność żyje w `layout_config` bloku → działa na edytowalnych layoutach
 * (kopia/nowy); na layoucie systemowym kanwa jest zablokowana (reorder pól niedostępny — patrz
 * TODO warstwy per-tenant). Read-side karty i builder używają TEGO SAMEGO helpera, żeby
 * kolejność w builderze == kolejność na karcie.
 */

/** Bezpieczny odczyt listy slugów spod klucza `settings` (np. `columnOrder`/`hiddenColumnIds`). */
export function parseSlugList(
  settings: Record<string, unknown> | undefined,
  key: string,
): string[] {
  const raw = settings?.[key];
  if (!Array.isArray(raw)) return [];
  return raw.filter((s): s is string => typeof s === 'string');
}

/**
 * Układa `defaultSlugs` wg zapisanej kolejności `order`:
 *  - najpierw slugi z `order` (tylko te istniejące w `defaultSlugs`, bez duplikatów),
 *  - potem pozostałe `defaultSlugs` w ich oryginalnej kolejności (nowe pola/kolumny widoczne
 *    z automatu, back-compat — dokładnie jak przy hide-liście).
 * Zwraca WSZYSTKIE slugi (bez filtra widoczności — hide stosuje wołający osobno).
 */
export function applyOrder(defaultSlugs: readonly string[], order: readonly string[]): string[] {
  if (!order || order.length === 0) return [...defaultSlugs];
  const known = new Set(defaultSlugs);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const s of order) {
    if (known.has(s) && !seen.has(s)) {
      seen.add(s);
      result.push(s);
    }
  }
  for (const s of defaultSlugs) {
    if (!seen.has(s)) result.push(s);
  }
  return result;
}

/**
 * Przesuwa slug `activeSlug` na pozycję `overSlug` w liście `current` (pełna kolejność, po
 * `applyOrder`). Zwraca NOWĄ listę slugów — gotową do zapisu w `settings[orderKey]`.
 * Bezpieczny: gdy któryś slug nieznany lub równy → zwraca `current` bez zmian.
 */
export function reorderSlugs(
  current: readonly string[],
  activeSlug: string,
  overSlug: string,
): string[] {
  if (activeSlug === overSlug) return [...current];
  const from = current.indexOf(activeSlug);
  const to = current.indexOf(overSlug);
  if (from < 0 || to < 0) return [...current];
  const next = [...current];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}
